import React, { useState, useEffect, useCallback } from 'react';
import { Box, Chip } from '@mui/material';
import { adminAPI } from '../../../utils/api';

/**
 * AnalystReviewsTab — read-only admin oversight of ONE analyst's review queue.
 *
 * Deliberately scoped to a single analyst per view: reviews (and the money
 * attached to them) from different analysts are never rendered together, so
 * there is no surface where analysts' earnings can be compared. Switching
 * analysts is an explicit navigation, not a dropdown next to a merged list.
 */

const DEFAULT_SLUG = 'fantasynerdboi';

const STATUS_COLORS = {
  awaiting_review: { bg: 'rgba(59,130,246,0.15)', color: '#3B82F6', label: 'AWAITING REVIEW' },
  completed: { bg: 'rgba(16,185,129,0.15)', color: '#10B981', label: 'COMPLETED' },
  pending_payment: { bg: 'rgba(100,116,139,0.15)', color: '#94A3B8', label: 'PENDING PAYMENT' },
  expired: { bg: 'rgba(239,68,68,0.15)', color: '#EF4444', label: 'EXPIRED' },
  refunded: { bg: 'rgba(100,116,139,0.15)', color: '#94A3B8', label: 'REFUNDED' },
};

const fmtDate = (iso) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
  });
};

const deadlineLabel = (review) => {
  if (review.status !== 'awaiting_review' || !review.deadlineAt) return null;
  const diff = new Date(review.deadlineAt) - Date.now();
  if (diff <= 0) return { text: 'OVERDUE', color: '#EF4444' };
  const h = Math.floor(diff / 3600000);
  return { text: h > 24 ? `${Math.floor(h / 24)}d ${h % 24}h left` : `${h}h left`, color: h < 6 ? '#F59E0B' : '#94A3B8' };
};

const AnalystReviewsTab = () => {
  const [slug, setSlug] = useState(DEFAULT_SLUG);
  const [slugInput, setSlugInput] = useState(DEFAULT_SLUG);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const load = useCallback(async (targetSlug) => {
    setLoading(true);
    setError(null);
    setOpenId(null);
    setDetail(null);
    try {
      const res = await adminAPI.getAnalystReviews(targetSlug);
      setData(res.data);
    } catch (err) {
      setError(err?.response?.data?.error || 'Failed to load queue');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load(slug);
  }, [slug, load]);

  const openReview = async (reviewId) => {
    if (openId === reviewId) {
      setOpenId(null);
      setDetail(null);
      return;
    }
    setOpenId(reviewId);
    setDetail(null);
    setDetailLoading(true);
    try {
      const res = await adminAPI.getAnalystReviewDetail(slug, reviewId);
      setDetail(res.data);
    } catch (err) {
      setDetail({ error: err?.response?.data?.error || 'Failed to load detail' });
    } finally {
      setDetailLoading(false);
    }
  };

  const analyst = data?.analyst;
  const reviews = data?.reviews || [];

  return (
    <Box>
      {/* Scope switcher — one analyst at a time, by explicit slug entry */}
      <Box component="form" onSubmit={(e) => { e.preventDefault(); if (slugInput.trim()) setSlug(slugInput.trim()); }} sx={{ display: 'flex', gap: '10px', marginBottom: '20px', alignItems: 'center' }}>
        <Box
          component="input"
          value={slugInput}
          onChange={(e) => setSlugInput(e.target.value)}
          placeholder="Analyst slug"
          sx={{
            background: '#0F172A', border: '1px solid #334155', borderRadius: '8px',
            color: '#F1F5F9', padding: '10px 14px', fontSize: '13px', width: '220px', outline: 'none',
          }}
        />
        <Box
          component="button"
          type="submit"
          sx={{
            background: '#3B82F6', border: 'none', borderRadius: '8px', color: '#fff',
            padding: '10px 18px', fontSize: '13px', fontWeight: 600, cursor: 'pointer',
          }}
        >
          Load queue
        </Box>
        <Box sx={{ fontSize: '12px', color: '#64748B' }}>Read-only. One analyst per view.</Box>
      </Box>

      {loading && <Box sx={{ textAlign: 'center', padding: '48px', color: '#94A3B8' }}>Loading queue…</Box>}
      {error && <Box sx={{ padding: '20px', color: '#EF4444' }}>{error}</Box>}

      {!loading && !error && analyst && (
        <>
          {/* Analyst header */}
          <Box sx={{ background: '#1E293B', border: '1px solid #334155', borderRadius: '12px', padding: '20px', marginBottom: '20px', display: 'flex', gap: '16px', alignItems: 'center', flexWrap: 'wrap' }}>
            {analyst.avatarUrl && (
              <Box
                component="img"
                src={analyst.avatarUrl}
                alt=""
                onError={(e) => { e.target.style.display = 'none'; }}
                sx={{ width: 48, height: 48, borderRadius: '50%', objectFit: 'cover' }}
              />
            )}
            <Box sx={{ flex: 1, minWidth: '200px' }}>
              <Box sx={{ fontSize: '16px', fontWeight: 700, color: '#F1F5F9' }}>{analyst.name}</Box>
              <Box sx={{ fontSize: '12px', color: '#94A3B8' }}>
                /{analyst.slug} · ${(analyst.priceInCents / 100).toFixed(2)}/review · {analyst.slaHours}h SLA
              </Box>
            </Box>
            <Chip
              label={analyst.stripeOnboardingComplete ? 'Stripe Connect: onboarded' : 'Stripe Connect: NOT onboarded'}
              size="small"
              sx={{
                background: analyst.stripeOnboardingComplete ? 'rgba(16,185,129,0.15)' : 'rgba(245,158,11,0.15)',
                color: analyst.stripeOnboardingComplete ? '#10B981' : '#F59E0B',
                fontWeight: 600,
              }}
            />
          </Box>

          {/* Queue */}
          {reviews.length === 0 ? (
            <Box sx={{ textAlign: 'center', padding: '48px', color: '#64748B' }}>No reviews yet for this analyst.</Box>
          ) : (
            reviews.map((review) => {
              const st = STATUS_COLORS[review.status] || STATUS_COLORS.pending_payment;
              const dl = deadlineLabel(review);
              const isOpen = openId === review.id;
              return (
                <Box key={review.id} sx={{ background: '#1E293B', border: '1px solid #334155', borderRadius: '12px', marginBottom: '10px', overflow: 'hidden' }}>
                  <Box
                    onClick={() => openReview(review.id)}
                    sx={{ padding: '14px 18px', display: 'flex', gap: '14px', alignItems: 'center', cursor: 'pointer', flexWrap: 'wrap', '&:hover': { background: '#26334A' } }}
                  >
                    <Chip label={st.label} size="small" sx={{ background: st.bg, color: st.color, fontWeight: 700, fontSize: '10px' }} />
                    <Box sx={{ fontSize: '13px', color: '#F1F5F9', fontWeight: 600 }}>
                      {review.requestedBy?.sleeperDisplayName || review.requestedBy?.sleeperUsername || review.requestedBy?.email || 'Unknown user'}
                    </Box>
                    <Box sx={{ fontSize: '12px', color: '#94A3B8' }}>
                      {(review.items || []).length} item{(review.items || []).length === 1 ? '' : 's'}
                      {review.amountPaidCents ? ` · $${(review.amountPaidCents / 100).toFixed(2)}` : ''}
                    </Box>
                    {dl && <Box sx={{ fontSize: '12px', fontWeight: 700, color: dl.color }}>{dl.text}</Box>}
                    <Box sx={{ marginLeft: 'auto', fontSize: '12px', color: '#64748B' }}>{fmtDate(review.createdAt)}</Box>
                  </Box>

                  {isOpen && (
                    <Box sx={{ borderTop: '1px solid #334155', padding: '18px', background: '#0F172A' }}>
                      {detailLoading && <Box sx={{ color: '#94A3B8', fontSize: '13px' }}>Loading detail…</Box>}
                      {detail?.error && <Box sx={{ color: '#EF4444', fontSize: '13px' }}>{detail.error}</Box>}
                      {detail && !detail.error && (
                        <>
                          <Box sx={{ display: 'flex', gap: '20px', flexWrap: 'wrap', marginBottom: '14px', fontSize: '12px', color: '#94A3B8' }}>
                            <span>Context sync: <b style={{ color: '#F1F5F9' }}>{detail.contextStatus}</b></span>
                            <span>Deadline: <b style={{ color: '#F1F5F9' }}>{fmtDate(detail.deadlineAt)}</b></span>
                            {detail.submittedAt && <span>Submitted: <b style={{ color: '#F1F5F9' }}>{fmtDate(detail.submittedAt)}</b></span>}
                            {detail.grades?.overallGrade != null && (
                              <span>Overall grade: <b style={{ color: '#3B82F6' }}>{detail.grades.overallGrade}/10</b></span>
                            )}
                          </Box>
                          {(detail.items || []).map((item, ix) => (
                            <Box key={item.id || ix} sx={{ border: '1px solid #334155', borderRadius: '10px', padding: '14px', marginBottom: '10px' }}>
                              <Box sx={{ fontSize: '11px', fontWeight: 700, color: '#3B82F6', textTransform: 'uppercase', marginBottom: '6px' }}>
                                {item.type}{item.grades?.overallGrade != null ? ` · ${item.grades.overallGrade}/10` : ''}
                              </Box>
                              {item.question && (
                                <Box sx={{ fontSize: '13px', color: '#CBD5E1', marginBottom: '8px' }}>“{item.question}”</Box>
                              )}
                              <Box sx={{ fontSize: '13px', color: item.analystResponse ? '#F1F5F9' : '#64748B', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
                                {item.analystResponse || 'No response yet.'}
                              </Box>
                            </Box>
                          ))}
                        </>
                      )}
                    </Box>
                  )}
                </Box>
              );
            })
          )}
        </>
      )}
    </Box>
  );
};

export default AnalystReviewsTab;
